import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, type ListFilters } from '../api/client';
import type { Issue, Severity, Status } from '../api/types';
import { IssueRow } from '../components/IssueRow';

const SEVERITIES: Severity[] = ['critical', 'major', 'minor', 'cosmetic'];
const STATUSES: Status[] = [
  'open',
  'in_progress',
  'fixed',
  'deferred',
  'closed',
  'duplicate',
  'wontfix',
];

export function IssueListPage() {
  const [search, setSearch] = useSearchParams();

  const filters = useMemo<ListFilters>(() => ({
    limit: 50,
    status: (search.get('status') as Status) || undefined,
    severity: (search.get('severity') as Severity) || undefined,
    component: search.get('component') || undefined,
    q: search.get('q') || undefined,
  }), [search]);

  const [items, setItems] = useState<Issue[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    api
      .list(filters)
      .then((r) => {
        setItems(r.items);
        setTotal(r.total);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Liste konnte nicht geladen werden'))
      .finally(() => setLoading(false));
  }, [filters]);

  function update(key: string, value: string) {
    const next = new URLSearchParams(search);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearch(next);
  }

  return (
    <>
      <div className="page-header">
        <h2>Liste</h2>
        <span className="breadcrumb">{total} issues · tenant arkturian</span>
      </div>

      <div className="filters">
        <select value={filters.status || ''} onChange={(e) => update('status', e.target.value)}>
          <option value="">alle status</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filters.severity || ''} onChange={(e) => update('severity', e.target.value)}>
          <option value="">alle severities</option>
          {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input
          placeholder="component"
          value={filters.component || ''}
          onChange={(e) => update('component', e.target.value)}
        />
        <input
          type="search"
          placeholder="text-search"
          value={filters.q || ''}
          onChange={(e) => update('q', e.target.value)}
        />
      </div>

      {loading && <div className="empty-state"><span className="spinner" /> &nbsp; lädt…</div>}
      {error && <div className="error">{error}</div>}
      {!loading && !error && items.length === 0 && (
        <div className="empty-state">keine issues passen zu den filtern</div>
      )}
      {!loading && !error && items.length > 0 && (
        <div className="issue-list">
          {items.map((i) => <IssueRow key={i.id} issue={i} />)}
        </div>
      )}
    </>
  );
}
